'use client';

import { Box, Button, CheckoutCard, Text } from '@instamenu/ui-system-next';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { DiscountCoupon } from '../discount-coupon/discount-coupon';
import { OrderItemList } from '../order-item-list/order-item-list';
import { OrderTotals } from '../order-totals/order-totals';
import { CheckoutLoyalty } from '../loyalty-status/checkout-loyalty';
import { PaymentMethod } from '../payment-method/payment-method';
import { orderSummaryBoxStyle, orderSummaryStyle } from './styles.css';
import { User } from '@instamenu/context-user';
import { Order } from '@instamenu/context-checkout';
import { FieldErrors } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { DateTime } from 'luxon';

export type OrderFormValues = {
  pickupLocation: boolean;
  deliveryAddress: boolean;
  itemCount: number;
  paymentMethod: boolean;
  restaurantOpen: boolean;
  scheduledAt: DateTime | null;
  userName: boolean;
  netOrderItemsTotal: number;
  deliveryQuote: boolean | undefined;
};

type OrderSummaryProps = {
  canCompleteOrder: boolean;
  errors: FieldErrors<OrderFormValues>;
  isTablet: boolean;
  loading: boolean;
  onSubmit: () => void;
  order: Order;
  user: User | null | undefined;
};

export const OrderSummary = observer(
  ({ isTablet, loading, order, user, errors, onSubmit }: OrderSummaryProps) => {
    const errorMessages = Object.values(errors).flatMap(({ message }) =>
      message ? [message] : [],
    );

    return (
      <CheckoutCard className={orderSummaryStyle}>
        <Box className={orderSummaryBoxStyle}>
          <Text variant="h3">Order Summary</Text>
          <OrderItemList />
          <Box css={{ height: 1, marginBottom: themeVars.pad[12], background: palette.gray50 }} />
          <DiscountCoupon />
          {isTablet && <CheckoutLoyalty />}
          <OrderTotals />

          {user && !isTablet && (
            <>
              <Box
                css={{ height: 1, marginBottom: themeVars.pad[12], background: palette.gray50 }}
              />
              <PaymentMethod />
            </>
          )}

          {errorMessages.length ? (
            <Box css={{ marginBottom: themeVars.pad[12] }}>
              {errorMessages.map((message, index) => (
                <Text variant="caption1" key={index} css={{ color: palette.warning }}>
                  {message}
                </Text>
              ))}
            </Box>
          ) : null}

          <Button
            loading={loading || order.loading}
            style={{ width: '100%' }}
            data-id="submit-order"
            onClick={onSubmit}
          >
            {!user
              ? 'Phone number required'
              : !order.paymentMethod?.id
                ? 'Payment method required'
                : 'Submit order'}
          </Button>
        </Box>
      </CheckoutCard>
    );
  },
);
